import { useEffect, useRef, useState } from "react";

/**
 * Meldet, wenn seit dem Laden dieses Tabs eine neue Fassung veröffentlicht wurde.
 *
 * WARUM (Noah, 18.09.2026): Zwei Kundinnenmeldungen am selben Tag. Katja: „manchmal lädt
 * noch die alte Version." Caroline: „die Website stürzt hier und da ab." Dahinter steckt
 * derselbe Vorgang. Jeder Lovable-Publish vergibt neue Dateinamen (am 18.09. bis 07:47 Uhr
 * fünfzehn Publishes, drei davon in drei Minuten). Ein offener Tab merkt davon nichts:
 * Er arbeitet mit alten Texten und alten Preisen weiter, und sobald er eine Seite öffnet,
 * deren Datei es nicht mehr gibt, lädt er sich hart neu — mitten in der Eingabe.
 *
 * Bisher gab es nur die reaktive Rettung in main.tsx (vite:preloadError → reload). Dieser
 * Hook ergänzt den aktiven Teil: version.json vergleichen und ein ANGEBOT machen, statt
 * unangekündigt zu springen.
 *
 * Bewusste Entscheidungen:
 *  - Geprüft wird beim Zurückkehren auf den Tab und danach höchstens alle 5 Minuten.
 *    Ein Intervall, das im Hintergrund weiterläuft, würde bei tagelang offenen Tabs
 *    tausende Anfragen erzeugen, ohne dass jemand hinsieht.
 *  - Nie automatisch neu laden. Wer gerade ein Formular ausfüllt, verliert sonst genau
 *    das, was diese Änderung schützen soll.
 *  - Jeder Fehler (offline, 404, kaputtes JSON) bleibt still: Das ist ein Komfortmerkmal,
 *    es darf nie ein Problem erzeugen, das ohne es nicht existiert hätte.
 */

/** Wird von Vite beim Build ersetzt (define in vite.config.ts). */
declare const __BUILD_ID__: string;

const MIN_ABSTAND_MS = 5 * 60 * 1000;

export function useNeueVersion(): { neueVersion: boolean; jetztLaden: () => void } {
  const [neueVersion, setNeueVersion] = useState(false);
  const letztePruefung = useRef(0);

  useEffect(() => {
    const eigeneId = typeof __BUILD_ID__ === "string" ? __BUILD_ID__ : "";
    // Im Dev-Server gibt es keine version.json — dann gar nicht erst prüfen.
    if (!eigeneId || import.meta.env.DEV) return;

    let abgemeldet = false;

    const pruefen = async () => {
      if (abgemeldet || document.visibilityState !== "visible") return;
      if (Date.now() - letztePruefung.current < MIN_ABSTAND_MS) return;
      letztePruefung.current = Date.now();

      try {
        const res = await fetch(`/version.json?t=${Date.now()}`, { cache: "no-store" });
        if (!res.ok) return;
        const daten = (await res.json()) as { buildId?: string };
        if (!abgemeldet && daten?.buildId && daten.buildId !== eigeneId) {
          setNeueVersion(true);
        }
      } catch {
        // offline oder Datei (noch) nicht da — beim nächsten Mal wieder.
      }
    };

    const beiSichtbarkeit = () => {
      if (document.visibilityState === "visible") void pruefen();
    };

    document.addEventListener("visibilitychange", beiSichtbarkeit);
    const timer = window.setInterval(() => void pruefen(), MIN_ABSTAND_MS);
    // Erste Prüfung bewusst verzögert: der Seitenstart soll frei bleiben.
    const start = window.setTimeout(() => void pruefen(), 30_000);

    return () => {
      abgemeldet = true;
      document.removeEventListener("visibilitychange", beiSichtbarkeit);
      window.clearInterval(timer);
      window.clearTimeout(start);
    };
  }, []);

  return {
    neueVersion,
    jetztLaden: () => window.location.reload(),
  };
}
